import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class WebSocketService {
  private socket: WebSocket;
  private messageSubject = new Subject<any>();
  public messages$ = this.messageSubject.asObservable();
  private messageQueue: any[] = [];
  private hasWarnedQueueLimit = false;

  /**
   * Ensure we have an open (or opening) socket without clobbering an existing one.
   * Use this when multiple features share the same WS connection.
   */
  isOpen(): boolean {
    return !!(this.socket && this.socket.readyState === WebSocket.OPEN);
  }

  ensureConnected(url: string): void {
    if (!this.socket) {
      this.connect(url);
      return;
    }

    if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
      return;
    }

    // CLOSED or CLOSING -> reconnect
    this.connect(url);
  }

  connect(url: string): void {
    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      console.log('✅ WebSocket connection established');
      this.messageSubject.next('CONNECTED');
      const queuedMessages = [...this.messageQueue];
      this.messageQueue = [];
      queuedMessages.forEach((msg) => this.send(msg));
    };

    this.socket.onmessage = (msg) => {
      // console.log('📨 WebSocket message received:', msg.data);
      const response = JSON.parse(msg.data);
      if (response && response.lead) {
        if (response.lead.statusCode === 500) {
          try {
            const errorBody = JSON.parse(response.lead.body || '{}');
            console.error('❌ Error from scoring service:', errorBody.message || 'Unknown error');
          } catch (e) {
            console.error('❌ Error parsing response body');
          }
          return;
        }
      }
      this.messageSubject.next(response);
    };

    this.socket.onerror = (err) => {
      console.error('❌ WebSocket error:', err);
      this.messageSubject.next('DISCONNECTED');
    };

    this.socket.onclose = () => {
      console.warn('🔌 WebSocket connection closed');
      this.messageSubject.next('DISCONNECTED');
    };
  }

  send(data: any): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
    } else {
      const dataType = typeof data === 'object' ? (data && data.action) || '[Object]' : data;
      if (!this.hasWarnedQueueLimit) {
        console.warn('⚠️ WebSocket not open. Messages are being queued.');
        this.hasWarnedQueueLimit = true;
      }
      this.messageQueue.push(data);
    }
  }

  close(): void {
    if (this.socket) {
      this.socket.close();
    }
  }

  connectAndSend(url: string, messages: any[]): void {
    // console.log('⚡ WebSocket connectAndSend() called with:', url, messages);

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('✅ WebSocket already open, sending messages directly');
      messages.forEach((msg) => {
        // console.log('📤 Sending message:', JSON.stringify(msg));
        this.send(msg);
      });
      return;
    }

    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      console.log('✅ WebSocket connection established');
      messages.forEach((msg) => {
        // console.log('📤 Sending message:', JSON.stringify(msg));
        this.send(msg);
      });
    };

    this.socket.onmessage = (msg) => {
      // console.log('📨 WebSocket message received:', msg.data);
      const response = JSON.parse(msg.data);
      if (response && response.lead) {
        if (response.lead.statusCode === 500) {
          try {
            const errorBody = JSON.parse(response.lead.body || '{}');
            console.error('❌ Error from scoring service:', errorBody.message || 'Unknown error');
          } catch (e) {
            console.error('❌ Error parsing response body');
          }
          return;
        }
      }
      this.messageSubject.next(response);
    };

    this.socket.onerror = (err) => {
      console.error('❌ WebSocket error:', err);
    };

    this.socket.onclose = () => {
      console.warn('🔌 WebSocket connection closed');
    };
  }

  sendUploadLeadsPayload(
    url: string,
    uploadedFiles: any[],
    projectId: string,
    entityId: string,
    authHeader: string,
    uploadViaTemplate: boolean = true
  ): void {
    console.log('📦 Preparing WebSocket payload...');
    const payload = {
      action: 'uploadLeads',
      data: {
        uploaded_files: uploadedFiles,
        project_id: projectId,
        entity_id: entityId,
        auth_header: authHeader,
        upload_via_template: uploadViaTemplate
      }
    };

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('✅ WebSocket already open, sending uploadLeads payload');
      console.log('📤 Sending payload:', JSON.stringify(payload));
      this.send(payload);
      return;
    }

    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      console.log('✅ WebSocket connection established for uploadLeads, sending payload');
      console.log('📤 Sending payload:', JSON.stringify(payload));
      this.send(payload);
    };

    this.socket.onmessage = (msg) => {
      console.log('📨 WebSocket message received:', msg.data);
      const response = JSON.parse(msg.data);
      this.messageSubject.next(response);
    };

    this.socket.onerror = (err) => {
      console.error('❌ WebSocket error:', err);
    };

    this.socket.onclose = () => {
      console.warn('🔌 WebSocket connection closed');
    };
  }


  sendMultiple(messages: any[]): void {
    messages.forEach((msg) => {
      // console.log('📤 Queued/Sending message:', JSON.stringify(msg));
      this.send(msg);
    });
  }
}